import { UserAgent, Inviter, Invitation } from "sip.js";
import type { Session, InviterOptions, InvitationAcceptOptions } from "sip.js";
import { getUserAgent } from "@/sip/sip-setup";
import { sipTelemetry } from "@/sip/sipTelemetry";
import { SessionState } from "sip.js";

import { useSipStore } from "@/store/sip";
import { getInvitationInfoFromPhoneNumber, addAction } from "@/api/apiclient/lead";
import { createCallLog } from "@/api/apiclient/call-log";
import { getQueue } from "@/api/apiclient/switchboard";
import {
  buildCallLogPayload,
  isLoggableCall,
  resolveQueueForExtension,
  type TerminatedCallSnapshot,
} from "@/sip/callLogRecord";
import { LeadActionParamsType } from "@/types/lead";

/**
 * Esito del tentativo di risposta. `no-call` non è un errore: è la chiamata che
 * non c'è più (la coda l'ha ritirata, il chiamante ha riagganciato). Distinguerlo
 * da un fallimento tecnico serve a dire all'operatore la cosa giusta invece di
 * non dirgli niente (GAIA-2664).
 */
export type AnswerOutcome = "answered" | "no-call";

const mapOriginToTerminator = (origin: string | null) => {
  switch (origin) {
    case "local":
      return "operator";
    case "remote-bye":
      return "customer";
    case "remote-reject":
      return "customer"; // o sistema
    case "timeout":
      return "system";
    case "error":
      return "system";
    default:
      return "unknown";
  }
};

/** Valori che finiscono in CallLogPayload.terminatedBy. Consumato da switchboardStatsVisuals. */
export type OutboundTerminator = ReturnType<typeof mapOriginToTerminator>;

let currentSession: Session | null = null;

// Asterisk apre ICE/DTLS già al 183 Session Progress: senza earlyMedia il
// browser applicherebbe l'answer SDP solo al 200 OK, a handshake DTLS del
// centralino ormai in backoff → 10-15 s di muto bidirezionale dopo la
// risposta. Vincolo SIP.js: con earlyMedia l'INVITE non deve forkare — vero
// dietro il B2BUA Asterisk (un solo early dialog).
export const OUTBOUND_INVITER_OPTIONS: InviterOptions = { earlyMedia: true };

// --- AVVIO CHIAMATA OUTBOUND ---
export const startCall = async (target: string) => {
  const userAgent = getUserAgent();
  const sipStore = useSipStore();

  if (!userAgent) {
    console.error("UserAgent non inizializzato.");
    return;
  }

  // resetta i campi temporanei per la chiamata in corso
  sipStore.clearEndCause();

  const sipUrl = import.meta.env.VITE_SIP_URL ?? "asterisk.harleydikkinson.com";
  const targetURI = UserAgent.makeURI(`sip:${target.replace("+", "00")}@${sipUrl}`);
  if (!targetURI) {
    console.error("URI di destinazione non valido.");
    return;
  }

  const inviter = new Inviter(userAgent, targetURI, OUTBOUND_INVITER_OPTIONS);
  setupEarlyMediaAudio(inviter);

  inviter.request.delegate = {
    onReject: (response: any) => {
      const code = response.message.statusCode;

      if (typeof code === "number" && code >= 500) {
        sipTelemetry.connectionFailed({ sipCode: code, transaction: "invite" });
      }

      // spegni subito ringback e marca motivo
      sipStore.stopOutgoingRingback();
      sipStore.stopRingtone();
      sipStore.isInCall = false;

      sipStore.setEndCause(code, "remote-reject");
      sipStore.callStatus = sipStore.lastEndCauseLabel;
    },
  };

  // campo custom per memorizzare il codice di failure
  (inviter as any)._failureStatusCode = undefined;

  sipStore.isInCall = true;
  if (!sipStore.callStartTime) {
    sipStore.startCallTimer(); // parte subito, prima che rispondano
  }

  // monitor comune per outbound
  monitorSession(inviter, "outgoing");

  try {
    await inviter.invite();
  } catch (error) {
    console.error("Errore durante l'avvio della chiamata:", error);
    sipStore.setEndCause(0, "error");
    sipStore.callStatus = sipStore.lastEndCauseLabel;
  }
};

// La supervisione (ascolta/sussurra/intervieni) non ha più un entry point qui:
// era `startMonitoring`, che le viste chiamavano direttamente finendo su un
// UserAgent inesistente nella loro window. Ora la stringa da comporre la
// costruisce la vista (sip/monitoringDialString) e la chiamata la esegue
// l'owner, raggiunto via comando `dial` (GAIA-2661).

// --- AUDIO REMOTO ---
type SdhWithPeerConnection = {
  peerConnection?: RTCPeerConnection;
};

type SdhWithPeerConnectionDelegate = {
  peerConnectionDelegate?: { ontrack?: (event: RTCTrackEvent) => void };
};

const attachRemoteAudioToCall = (session: Session) => {
  const sdh = session.sessionDescriptionHandler as SdhWithPeerConnection | undefined;
  const peerConnection = sdh?.peerConnection;
  if (!peerConnection) return;

  const remoteStream = new MediaStream();
  peerConnection.getReceivers().forEach((receiver) => {
    if (receiver.track.kind === "audio") {
      remoteStream.addTrack(receiver.track);
    }
  });

  const sipStore = useSipStore();
  sipStore.attachRemoteAudio(remoteStream);
};

/**
 * Aggancia l'audio remoto appena la peer connection riceve la prima track,
 * cioè quando viene applicata l'answer SDP — col 183 questo avviene durante
 * lo squillo (early media): l'operatore sente l'audio reale di rete (squillo
 * del carrier, messaggi, segreteria) e ICE/DTLS si completano prima della
 * risposta. Il ringback sintetico locale viene fermato in quel momento.
 * Da chiamare prima di inviter.invite(), altrimenti il delegate non vede
 * la creazione del SessionDescriptionHandler.
 */
export const setupEarlyMediaAudio = (session: Session): void => {
  session.delegate = {
    onSessionDescriptionHandler: (sdh) => {
      (sdh as SdhWithPeerConnectionDelegate).peerConnectionDelegate = {
        ontrack: () => {
          const sipStore = useSipStore();
          sipStore.stopOutgoingRingback();
          attachRemoteAudioToCall(session);
        },
      };
    },
  };
};

// --- RISPOSTA CHIAMATA INBOUND ---
/**
 * Accetta l'INVITE in arrivo. **Rilancia** in caso di errore.
 *
 * Prima di GAIA-2664 l'errore finiva in un `console.error` e nient'altro: se
 * `accept()` falliva — tipicamente perché `getUserMedia` non concede il
 * microfono, o l'audio è occupato da un'altra applicazione — l'operatore vedeva
 * il tasto verde non fare assolutamente nulla, cliccava di nuovo, e la chiamata
 * si perdeva senza che nessuno sapesse perché. Chi chiama DEVE poter mostrare
 * l'errore: una risposta fallita in silenzio è indistinguibile da un click
 * ignorato.
 */
export const answerCall = async (invitation: Invitation): Promise<void> => {
  const acceptOptions: InvitationAcceptOptions = {
    sessionDescriptionHandlerOptions: {
      constraints: {
        audio: true,
        video: false,
      },
    },
  };

  // monitorSession per inbound viene già agganciato in setupSIP.onInvite
  await invitation.accept(acceptOptions);
};

// --- FINE CHIAMATA (usato dal bottone) ---
export const endCall = async () => {
  const sipStore = useSipStore();

  if (!currentSession) {
    console.error("Nessuna chiamata in corso.");
    return;
  }

  try {
    // chiusura voluta dall’operatore
    sipStore.setEndCause(null, "local");

    // OUTBOUND (Inviter)
    if (currentSession instanceof Inviter) {
      if (
        currentSession.state === SessionState.Initial ||
        currentSession.state === SessionState.Establishing
      ) {
        // sta solo squillando → CANCEL
        await currentSession.cancel();
      } else {
        // già in conversazione → BYE
        await currentSession.bye();
      }
    }
    // INBOUND (Invitation) – opzionale, di solito usi declineCall
    else if (currentSession instanceof Invitation) {
      if (
        currentSession.state === SessionState.Initial ||
        currentSession.state === SessionState.Establishing
      ) {
        await currentSession.reject();
      } else {
        await currentSession.bye();
      }
    }
  } catch (error) {
    console.error("Errore durante la terminazione della chiamata:", error);
  } finally {
    // failsafe: in ogni caso ferma localmente lo stato “impegnato”
    sipStore.stopRingtone();
    sipStore.stopOutgoingRingback();
    sipStore.isInCall = false;
    sipStore.isCallEstablished = false;
    currentSession = null;
  }
};

export const monitorSession = (session: Session, direction: "incoming" | "outgoing") => {
  const sipStore = useSipStore();

  currentSession = session;
  sipStore.callDirection = direction;
  // Per outgoing è qui che callDirection diventa valido per la prima volta:
  // serve persistere subito lo snapshot di recovery (per inbound è già fatto in setIncomingCall).
  sipStore.persistRecoverySnapshot();

  session.stateChange.addListener(async (state) => {
    switch (state) {
      case SessionState.Initial:
        sipStore.isInCall = false;
        if (direction === "incoming") {
          sipStore.callStatus = "Sta squillando";
          sipStore.startRingtone();
        } else {
          sipStore.callStatus = "Chiamata in uscita...";
          sipStore.startOutgoingRingback();
        }
        break;

      case SessionState.Establishing:
        sipStore.callStatus = "Connessione...";
        if (direction === "outgoing") {
          sipStore.startOutgoingRingback();
        }
        break;

      case SessionState.Established:
        sipStore.isInCall = true;
        sipStore.isCallEstablished = true;
        sipStore.callStatus = "In chiamata";
        sipStore.wasCallEstablished = true;

        sipStore.stopRingtone();
        sipStore.stopOutgoingRingback();

        sipStore.triggerAnswerBeep();

        // inbound: callStartTime è null → parte ora
        // outbound: callStartTime è già valorizzato → NON resettare
        if (!sipStore.callStartTime) {
          sipStore.startCallTimer();
        }

        attachRemoteAudioToCall(session);

        // --- TRACK EVENT QUI ---
        if (direction === "outgoing") {
          const target = sipStore.phoneNumber; // numero che hai chiamato (in formato "+39...")
          if (target) {
            trackCallEvent("out", target).catch((err) =>
              console.error("Errore trackCallEvent outbound:", err)
            );
          }
        } else {
          const target = (session as any).remoteIdentity?.uri?.user;
          if (target) {
            trackCallEvent("in", target).catch((err) =>
              console.error("Errore trackCallEvent inbound:", err)
            );
          }
        }
        break;

      case SessionState.Terminating:
        sipStore.stopRingtone();
        sipStore.stopOutgoingRingback();
        sipStore.callStatus = "Terminazione in corso...";
        break;

      case SessionState.Terminated: {
        sipStore.stopRingtone();
        sipStore.stopOutgoingRingback();

        if (!sipStore.lastEndCauseOrigin) {
          // se eri in chiamata → probabile BYE remoto
          // se non sei mai entrato in Established → timeout / nessuna risposta
          const origin =
            sipStore.callStartTime && sipStore.callDuration > 0 ? "remote-bye" : "timeout";

          sipStore.setEndCause(null, origin);
        }

        // --- CALL LOGGING ---
        // Viene loggato tutto ciò che arriva a Terminated, comprese le inbound
        // non risposte (durata 0): sono proprio quelle che interessano nei KPI.
        //
        // La fotografia è SINCRONA e precede qualunque await: da qui in avanti
        // nessun campo può più essere riscritto da ciò che accade durante la
        // lookup della coda. `direction` arriva dal parametro di monitorSession e
        // non da `sipStore.callDirection`, che descrive la chiamata CORRENTE: se
        // l'operatore ne ha già avviata un'altra, lo store parla di quella e la
        // riga di questa finiva registrata con la direzione sbagliata (GAIA-2664).
        const endedAt = new Date().toISOString();
        const record: TerminatedCallSnapshot = {
          extension: sipStore.extension,
          direction,
          phoneNumber: sipStore.phoneNumber,
          durationSeconds: sipStore.callDuration,
          // Una inbound persa non è mai partita: startTime coincide con endTime.
          startTime: sipStore.callStartTime
            ? new Date(sipStore.callStartTime).toISOString()
            : endedAt,
          endTime: endedAt,
          terminatedBy: mapOriginToTerminator(sipStore.lastEndCauseOrigin),
        };

        // Il teardown dello stato va eseguito ORA, prima della lookup: azzera
        // timer, snapshot di recovery e riferimento di sessione. Rimandato dopo un
        // await colpirebbe la chiamata successiva — `currentSession = null` le
        // toglieva la sessione, rendendola impossibile da riagganciare dalla
        // barra. La guardia non protegge da chiamate simultanee (il centralino non
        // le consente): protegge dalla continuazione asincrona di una sessione
        // già terminata, che è un'altra cosa.
        //
        // `currentSession` nullo NON è un caso da saltare: è ciò che lascia
        // `endCall()` quando l'operatore riaggancia dal bottone, e il cleanup
        // completo (timer, snapshot, dtmf) lo fa solo questo ramo. Si salta solo
        // se una sessione DIVERSA è ormai quella corrente.
        const supersededByNewerSession = currentSession !== null && currentSession !== session;
        if (!supersededByNewerSession) {
          sipStore.callStatus = sipStore.lastEndCauseLabel;
          sipStore.isInCall = false;
          sipStore.hangupCall();
          sipStore.wasCallEstablished = false;
          currentSession = null;
        }

        if (!isLoggableCall(record)) break;

        let queue: string | null = null;
        try {
          queue = resolveQueueForExtension(await getQueue(), record.extension);
        } catch (err) {
          console.error("Error fetching queue info for log:", err);
        }

        // Fire & Forget log
        createCallLog(buildCallLogPayload(record, queue)).catch((err) =>
          console.error("Error creating call log:", err)
        );
        break;
      }
    }
  });
};

export const trackCallEvent = async (direction: string, target: string) => {
  if (!direction || !target) return;

  const tempLead = await getInvitationInfoFromPhoneNumber(target);
  if (!tempLead.data) return;

  return addAction({
    description: ``,
    entityId: `${tempLead.data.id}`,
    entityType: "Invitation",
    name: direction === "in" ? "Chiamata - Inbound" : "Chiamata - Outbound",
    status: "Executed",
    type: direction === "in" ? "InboundCall" : "OutboundCall",
  } as LeadActionParamsType);
};

// Toni codificabili come RTP telephone-event (RFC 4733): 0-9, *, #.
const isRtpTone = (tone: string): boolean => /^[0-9*#]$/.test(tone);

// Segnali trasmissibili. Il "+" (long-press dello "0" sul tastierino) NON è
// un evento RTP — RTCDTMFSender.insertDTMF lo rifiuterebbe — ma può essere
// inviato letteralmente come segnale via SIP INFO (application/dtmf-relay).
const isSendableSignal = (tone: string): boolean => isRtpTone(tone) || tone === "+";

type SdhWithDtmf = {
  sendDtmf?: (tones: string, options?: { duration?: number; interToneGap?: number }) => boolean;
};

/**
 * Manda un singolo tono DTMF al peer remoto della chiamata corrente.
 *
 * Strategy: tenta prima RFC 4733 via RTCDTMFSender (esposto da
 * SessionDescriptionHandler.sendDtmf); se non disponibile o se il
 * metodo ritorna false, fa fallback a SIP INFO con content-type
 * application/dtmf-relay.
 *
 * Il parametro `session` è esposto per testabilità: in produzione non
 * viene passato e si usa la `currentSession` interna del modulo.
 */
export const sendDtmf = async (
  tone: string,
  session: Session | null = currentSession
): Promise<void> => {
  if (!isSendableSignal(tone)) {
    throw new Error(`sendDtmf: tono "${tone}" non valido`);
  }
  if (!session) {
    throw new Error("sendDtmf: nessuna sessione attiva");
  }
  if (session.state !== SessionState.Established) {
    throw new Error("sendDtmf: la sessione non è in stato Established");
  }

  // A — RFC 4733 (RTP telephone-event) via RTCDTMFSender.
  // Solo per i toni codificabili come evento RTP: il "+" NON lo è e va
  // inviato esclusivamente via SIP INFO (path B), altrimenti insertDTMF
  // lancerebbe InvalidCharacterError.
  const sdh = session.sessionDescriptionHandler as SdhWithDtmf | undefined;

  if (sdh && typeof sdh.sendDtmf === "function") {
    // insertDTMF può lanciare sincrono se il RTCDTMFSender non è pronto
    // (es. IVR con early media / DTLS in backoff — GAIA-2588). In quel caso
    // NON abortiamo il send: logghiamo e proseguiamo col fallback SIP INFO,
    // così il tono parte comunque.
    try {
      const sentViaRtp = sdh.sendDtmf(tone, { duration: 100, interToneGap: 70 });
      if (sentViaRtp) {
        return;
      }
    } catch (error) {
      console.warn("[sendDtmf] RTP DTMF non riuscito, fallback a SIP INFO:", error);
    }
  }

  // B — SIP INFO con application/dtmf-relay (unica via per "+")
  await session.info({
    requestOptions: {
      body: {
        contentDisposition: "render",
        contentType: "application/dtmf-relay",
        content: `Signal=${tone}\r\nDuration=160`,
      },
    },
  });
};
